import React, { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import Layout from "./components/Layout";
import ChatPage from "./pages/ChatPage";
import LoginPage from "./pages/LoginPage";
import ProtectedRoute from "./components/ProtectedRoute";
import { ConversationProvider } from "./context/ConversationContext";
import { APIProvider } from "./context/APIContext";
import { msalConfig } from "./config/authConfig";
import CreateProjectPage from "./pages/Projects Page/CreateProjectPage";
import AllProjectsPage from "./pages/Projects Page/AllProjectsPage";
import ProjectPage from "pages/Projects Page/ProjectPage";
import { Helmet } from "react-helmet";
import SharedChatPage from "./pages/SharedChatPage";

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);
 
const App: React.FC = () => {
  const companyName = process.env.REACT_APP_COMPANY_NAME || "InternalGpt";
  const faviconUrl = process.env.REACT_APP_IMAGE_URL;
 
  // More reliable favicon implementation
  useEffect(() => {
    // Make sure the favicon URL is valid
    if (!faviconUrl) return;

    // Create new favicon link element
    const link = document.createElement("link");
    link.rel = "icon";
    link.href = faviconUrl;

    // Remove any existing favicon links
    const existingLinks = document.querySelectorAll("link[rel*='icon']");
    existingLinks.forEach((el) => el.parentNode?.removeChild(el));

    // Add the new favicon link
    document.head.appendChild(link);
    
    window.addEventListener("load", () => {
      sessionStorage.removeItem("session_id");
      sessionStorage.removeItem("thread_id");
    });
  }, [faviconUrl]);

  return (
    <>
      <Helmet>
        <title>{companyName}</title>
        <meta name="description" content={companyName} />
        {/* Adding favicon here as well for redundancy */}
        <link rel="icon" href={faviconUrl} />
      </Helmet>
      <MsalProvider instance={msalInstance}>
        <AuthenticatedTemplate>
          <APIProvider>
            <ConversationProvider>
              <Routes>
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<ChatPage />} />
                  <Route path="/c/:conversationId" element={<ChatPage />} />
                  <Route
                    path="/projects/:projectId"
                    element={<ProjectPage />}
                  />
                  <Route
                    path="/create-project"
                    element={<CreateProjectPage />}
                  />
                  <Route path="/projects" element={<AllProjectsPage />} />
                </Route>
                <Route path="/share/:conversationId" element={<SharedChatPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </ConversationProvider>
          </APIProvider>
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/share/:conversationId" element={<SharedChatPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </UnauthenticatedTemplate>
      </MsalProvider>
    </>
  );
};

export default App;
